import { defineStore } from 'pinia'
import { ref } from 'vue'

export interface SnackbarState {
  show: boolean
  message: string
  type: 'success' | 'error' | 'info' | 'warning'
  timeout: number
}

export const useSnackbarStore = defineStore('snackbar', () => {
  const snackbar = ref<SnackbarState>({
    show: false,
    message: '',
    type: 'info',
    timeout: 4000
  })

  /**
   * Whether error messages are being dropped.
   *
   * Set while a session is ending. Every request that was in flight fails at
   * once and each one wants to report itself, which would bury the auth
   * store's session-expired warning under a message the user cannot act on.
   */
  const errorsSuspended = ref(false)

  function showSnackbar(
    message: string,
    type: SnackbarState['type'] = 'info',
    timeout = 4000
  ) {
    if (type === 'error' && errorsSuspended.value) return

    snackbar.value = { show: true, message, type, timeout }
  }

  function hideSnackbar() {
    snackbar.value.show = false
  }

  /** Starts dropping error messages. Called when a session ends. */
  function suspendErrors() {
    errorsSuspended.value = true
  }

  /** Resumes error messages. Called when a new session starts. */
  function resumeErrors() {
    errorsSuspended.value = false
  }

  // Convenience methods
  const success = (message: string, timeout = 4000) => showSnackbar(message, 'success', timeout)
  const error = (message: string, timeout = 4000) => showSnackbar(message, 'error', timeout)
  const info = (message: string, timeout = 4000) => showSnackbar(message, 'info', timeout)
  const warning = (message: string, timeout = 4000) => showSnackbar(message, 'warning', timeout)

  return {
    snackbar,
    errorsSuspended,
    hideSnackbar,
    suspendErrors,
    resumeErrors,
    success,
    error,
    info,
    warning
  }
})
